import { useMemo } from 'react'

import type { ErrorLike } from '@apollo/client'
import { useQuery } from '@apollo/client/react'
import { shallowEqual } from 'react-redux'

import { graphql } from '~publish/graphql'
import type { FragmentOf, ResultOf } from '~publish/graphql'
import { sanitizeNullableArray } from '~publish/helpers/typeGuards'
import type { Profile } from '~publish/legacy/profile-sidebar/types'
import { useAppSelector } from '~publish/legacy/store'
import type { RootState } from '~publish/legacy/store'
import { CommentCard_Channel } from '~publish/pages/Community/Comments/CommentCard'
import { OriginalPostCard_Channel } from '~publish/pages/Community/Comments/CommentCard/OriginalPostCard'
import { CommentsForPost_Channel } from '~publish/pages/Community/Comments/CommentsByPost/CommentsForPost/CommentsForPost'
import { UseViewMode_Channel } from '~publish/pages/Community/Comments/CommentsByPost/hooks/useViewMode'
import { CommentsList_Channel } from '~publish/pages/Community/Comments/CommentsList'
import { UseCommentsListData_Channel } from '~publish/pages/Community/Comments/CommentsList/hooks/useCommentsListData'
import { useCommunityConfig } from '~publish/pages/Community/CommunityConfig'
import { EngagementAvatar_Channel } from '~publish/pages/Community/components/EngagementAvatar'
import { EngagementReplyForm_Channel } from '~publish/pages/Community/components/EngagementReplyForm/fragments/engagementReplyFormChannel'
import { UseBackfillLoadingBanner_Channel } from '~publish/pages/Community/hooks/backfill/useBackfillLoadingBanner'
import { UseChannelCommentCounts_Channel } from '~publish/pages/Community/hooks/comments/useChannelCommentCounts'
import { MentionCard_Channel } from '~publish/pages/Community/Mentions/MentionCard/MentionCard'
import { MentionsList_Channel } from '~publish/pages/Community/Mentions/MentionsList/MentionsList'

export const CommentsChannelNavChannelFragment = graphql(`
  fragment CommentsChannelNavChannelFragment on Channel @_unmask {
    id
    name
    displayName
    metadata {
      ... on BlueskyMetadata {
        serverUrl
      }
    }
    service
    avatar
    serverUrl
    locationData {
      location
    }
    isLocked
    isDisconnected
    type
    apiVersion
    scopes
  }
`)

export const GetCommentsSidebarInfo = graphql(
  `
    query GetCommentsSidebarInfo {
      # TODO: channels should be a top-level query
      account {
        id
        currentOrganization {
          id
          channels(input: { filter: { product: publish, isLocked: false } }) {
            id
            name
            displayName
            service
            avatar
            allowedActions
            comments {
              lastCheckedAt
              nextUpdateAt
            }
            ...CommentsChannelNavChannelFragment
            ...EngagementAvatar_Channel
            ...CommentsList_Channel
            ...CommentCard_Channel
            ...CommentsForPost_Channel
            ...OriginalPostCard_Channel
            ...UseViewMode_Channel
            ...UseCommentsListData_Channel
            ...UseBackfillLoadingBanner_Channel
            ...UseChannelCommentCounts_Channel
            ...MentionsList_Channel
            ...MentionCard_Channel
            ...EngagementReplyForm_Channel
          }
        }
      }
    }
  `,
  [
    CommentsChannelNavChannelFragment,
    EngagementAvatar_Channel,
    CommentsList_Channel,
    CommentCard_Channel,
    CommentsForPost_Channel,
    OriginalPostCard_Channel,
    UseViewMode_Channel,
    UseCommentsListData_Channel,
    UseBackfillLoadingBanner_Channel,
    UseChannelCommentCounts_Channel,
    MentionsList_Channel,
    MentionCard_Channel,
    EngagementReplyForm_Channel,
  ],
)

export type CommentsChannelNav = FragmentOf<
  typeof CommentsChannelNavChannelFragment
>

/**
 * The channel node returned by {@link GetCommentsSidebarInfo}, derived from the
 * gql.tada document with every consumer fragment composed in. This is the
 * shared "Channel" shape the sidebar and the Community list/card readers
 * consume.
 */
export type CommentsSidebarChannel = NonNullable<
  NonNullable<
    ResultOf<typeof GetCommentsSidebarInfo>['account']
  >['currentOrganization']
>['channels'][number]

export function useCommentsChannels(): {
  channels: CommentsSidebarChannel[]
  loading: boolean
  error: ErrorLike | undefined
} {
  const {
    data: channelsData,
    loading,
    error,
  } = useQuery(GetCommentsSidebarInfo)

  const { config } = useCommunityConfig()
  const { isSupportedInComments, isSupportedInEngage } = config

  // `shallowEqual` keeps the array reference stable across unrelated Redux
  // dispatches — without it, the `.map(...)` produces a new array each
  // dispatch and cascades through the `useMemo` deps below.
  const profilesIdOrder = useAppSelector(
    (state: RootState) =>
      state.profileSidebar.profiles?.map((profile: Profile) => profile.id) ??
      [],
    shallowEqual,
  )

  const rawChannels = channelsData?.account?.currentOrganization?.channels

  const channels = useMemo(
    () =>
      sanitizeNullableArray(rawChannels).toSorted((a, b) => {
        // First priority: Supported channels (Comments)
        const aIsSupported = isSupportedInComments(a)
        const bIsSupported = isSupportedInComments(b)
        if (aIsSupported !== bIsSupported) {
          return aIsSupported ? -1 : 1
        }

        // Second priority: Engagement channels (Facebook, Instagram)
        const aIsEngagement = isSupportedInEngage(a)
        const bIsEngagement = isSupportedInEngage(b)
        if (aIsEngagement !== bIsEngagement) {
          return aIsEngagement ? -1 : 1
        }

        // Third priority: Original profile order
        // TODO: Since we don't have order implemented on the GraphQL API,
        // we sort by the order of the profiles in the sidebar stored in Redux.
        // This should go away once we have order implemented on the API.
        const aIndex = profilesIdOrder.indexOf(a.id)
        const bIndex = profilesIdOrder.indexOf(b.id)
        if (aIndex === -1 && bIndex === -1) return 0
        if (aIndex === -1) return 1
        if (bIndex === -1) return -1
        return aIndex - bIndex
      }),
    [rawChannels, isSupportedInComments, isSupportedInEngage, profilesIdOrder],
  )

  return useMemo(
    () => ({
      channels,
      loading,
      error,
    }),
    [channels, loading, error],
  )
}
